import { z } from 'zod';
import { documentLockAttemptSchema } from './document-lock.js';
import { idSchema, isoDateTimeSchema } from './common.js';
import { callDirectionSchema, callOutcomeSchema, documentStatusSchema, documentTypeSchema, noteVisibilitySchema, } from './enums.js';
import { paginationMetaSchema, paginationQuerySchema } from './pagination.js';
// ---------------------------------------------------------------------------
// Notizen
// ---------------------------------------------------------------------------
export const customerNoteSchema = z.object({
    id: idSchema,
    customerId: idSchema,
    authorId: idSchema,
    authorName: z.string(),
    contactId: idSchema.nullable(),
    contactName: z.string().nullable(),
    subject: z.string(),
    body: z.string(),
    visibility: noteVisibilitySchema,
    pinned: z.boolean(),
    occurredAt: isoDateTimeSchema,
    followUpAt: isoDateTimeSchema.nullable(),
    followUpDoneAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const customerNoteWriteSchema = z.object({
    contactId: idSchema.nullish(),
    subject: z.string().trim().min(1).max(200),
    body: z.string().trim().min(1).max(10_000),
    visibility: noteVisibilitySchema.default('TEAM'),
    pinned: z.boolean().default(false),
    occurredAt: isoDateTimeSchema.optional(),
    followUpAt: isoDateTimeSchema.nullish(),
});
export const customerNoteUpdateSchema = customerNoteWriteSchema.partial().extend({
    followUpDoneAt: isoDateTimeSchema.nullish(),
});
export const customerNoteListQuerySchema = paginationQuerySchema.extend({
    pinnedFirst: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .default(true)
        .transform((value) => value === true || value === 'true'),
});
export const customerNoteListResponseSchema = z.object({
    data: z.array(customerNoteSchema),
    meta: paginationMetaSchema,
});
// ---------------------------------------------------------------------------
// Telefonnotizen
// ---------------------------------------------------------------------------
export const callLogSchema = z.object({
    id: idSchema,
    customerId: idSchema,
    userId: idSchema,
    userName: z.string(),
    contactId: idSchema.nullable(),
    contactName: z.string().nullable(),
    documentId: idSchema.nullable(),
    documentNumber: z.string().nullable(),
    direction: callDirectionSchema,
    outcome: callOutcomeSchema,
    subject: z.string(),
    notes: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    occurredAt: isoDateTimeSchema,
    durationSeconds: z.number().int().nullable(),
    followUpAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const callLogWriteSchema = z.object({
    contactId: idSchema.nullish(),
    documentId: idSchema.nullish(),
    direction: callDirectionSchema,
    outcome: callOutcomeSchema.default('REACHED'),
    subject: z.string().trim().min(1).max(200),
    notes: z.string().trim().max(10_000).nullish(),
    phoneNumber: z.string().trim().max(50).nullish(),
    occurredAt: isoDateTimeSchema,
    durationSeconds: z.number().int().min(0).max(86_400).nullish(),
    followUpAt: isoDateTimeSchema.nullish(),
});
export const callLogUpdateSchema = callLogWriteSchema.partial();
export const callLogListQuerySchema = paginationQuerySchema;
export const callLogListResponseSchema = z.object({
    data: z.array(callLogSchema),
    meta: paginationMetaSchema,
});
// ---------------------------------------------------------------------------
// Einheitliche Aktivitäten-Timeline
// ---------------------------------------------------------------------------
export const activityTypeSchema = z.enum([
    'CALL_INBOUND',
    'CALL_OUTBOUND',
    'CONVERSATION',
    'CALLBACK_PLANNED',
    'CALLBACK_COMPLETED',
    'VISIT',
    'MEETING',
    'NOTE',
    'QUOTE',
    'ORDER',
    'PORTAL_ORDER',
    'DELIVERY_NOTE',
    'INVOICE',
    'CREDIT_NOTE',
    'CANCELLATION',
    'EMAIL',
    'COMPLAINT',
    'RETURN',
    'TASK',
    'FOLLOW_UP',
    'STATUS_CHANGE',
    'OTHER',
]);
export const activitySourceSchema = z.enum([
    'MANUAL',
    'LEGACY_NOTE',
    'LEGACY_CALL',
    'CTI',
    'DOCUMENT',
    'DOCUMENT_EMAIL',
    'CUSTOMER_PORTAL',
    'QUESTIONNAIRE',
    'SALES_TASK',
    'SYSTEM',
]);
export const salesContactOutcomeSchema = z.enum([
    'REACHED',
    'NOT_REACHED',
    'CALLBACK_REQUESTED',
    'CALLBACK_SCHEDULED',
    'ORDER_EXPECTED',
    'QUOTE_REQUESTED',
    'SAMPLE_REQUESTED',
    'NO_CURRENT_NEED',
    'CONTACT_LATER',
    'CONTACT_CHANGED',
    'TEMPORARILY_CLOSED',
    'CUSTOMER_LOST',
    'INVALID_CONTACT_DATA',
    'COMPLAINT_OR_PROBLEM',
    'OTHER',
]);
export const MANUAL_ACTIVITY_TYPES = [
    'CALL_INBOUND',
    'CALL_OUTBOUND',
    'CONVERSATION',
    'CALLBACK_PLANNED',
    'CALLBACK_COMPLETED',
    'VISIT',
    'MEETING',
    'NOTE',
    'EMAIL',
    'COMPLAINT',
    'RETURN',
    'FOLLOW_UP',
    'OTHER',
];
export const manualActivityTypeSchema = z.enum(MANUAL_ACTIVITY_TYPES);
export const activityDocumentPayloadSchema = z.object({
    documentType: documentTypeSchema,
    documentStatus: documentStatusSchema,
    number: z.string(),
    grossTotal: z.string(),
    currency: z.string(),
});
export const activityCallPayloadSchema = z.object({
    direction: callDirectionSchema,
    outcome: z.union([callOutcomeSchema, salesContactOutcomeSchema]).nullable(),
    status: z.enum(['RINGING', 'ANSWERED', 'MISSED', 'VOICEMAIL', 'FAILED']).nullable(),
    durationSeconds: z.number().int().nullable(),
});
export const activityNotePayloadSchema = z.object({
    visibility: noteVisibilitySchema,
    pinned: z.boolean(),
});
export const activityReferenceSchema = z.object({
    entityType: z.string().min(1).max(80),
    entityId: z.string().min(1).max(200),
    label: z.string().max(240).nullable(),
    href: z.string().max(500).nullable(),
});
export const activitySchema = z.object({
    id: z.string().min(8).max(200),
    customerId: idSchema,
    contactId: idSchema.nullable(),
    contactName: z.string().nullable(),
    type: activityTypeSchema,
    occurredAt: isoDateTimeSchema,
    title: z.string(),
    summary: z.string().nullable(),
    detailNote: z.string().nullable(),
    responsibleUserId: idSchema.nullable(),
    responsibleUserName: z.string().nullable(),
    responsibleTeamId: idSchema.nullable(),
    responsibleTeamName: z.string().nullable(),
    direction: callDirectionSchema.nullable(),
    outcome: salesContactOutcomeSchema.nullable(),
    source: activitySourceSchema,
    sourceLabel: z.string().nullable(),
    isPersonalContact: z.boolean(),
    isSuccessfulContact: z.boolean(),
    actorName: z.string().nullable(),
    createdById: idSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
    revisionCount: z.number().int().min(0),
    reference: activityReferenceSchema.nullable(),
    document: activityDocumentPayloadSchema.nullable(),
    documentLockAttempt: documentLockAttemptSchema.optional(),
    call: activityCallPayloadSchema.nullable(),
    note: activityNotePayloadSchema.nullable(),
});
const activityWriteFieldsSchema = z.object({
    contactId: idSchema.nullish(),
    type: manualActivityTypeSchema,
    occurredAt: isoDateTimeSchema,
    responsibleUserId: idSchema.nullish(),
    direction: callDirectionSchema.nullish(),
    outcome: salesContactOutcomeSchema.nullish(),
    summary: z.string().trim().min(1).max(500),
    note: z.string().trim().max(10_000).nullish(),
    sourceEntityType: z.string().trim().min(1).max(80).nullish(),
    sourceEntityId: z.string().trim().min(1).max(200).nullish(),
});
export const activityWriteSchema = activityWriteFieldsSchema.superRefine((value, context) => {
    const isCall = value.type === 'CALL_INBOUND' || value.type === 'CALL_OUTBOUND';
    if (isCall && (value.direction === null || value.direction === undefined)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['direction'],
            message: 'ACTIVITY_DIRECTION_REQUIRED',
        });
    }
    if (value.type === 'CALLBACK_PLANNED' && Date.parse(value.occurredAt) <= Date.now()) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['occurredAt'],
            message: 'ACTIVITY_FUTURE_DATE_REQUIRED',
        });
    }
});
export const activityUpdateSchema = activityWriteFieldsSchema.partial();
export const activityRevisionSchema = z.object({
    id: idSchema,
    version: z.number().int().min(1),
    snapshot: z.record(z.unknown()),
    changedByUserId: idSchema,
    changedByName: z.string(),
    changedAt: isoDateTimeSchema,
});
export const activityRevisionListResponseSchema = z.object({
    data: z.array(activityRevisionSchema),
});
export const activityListQuerySchema = paginationQuerySchema.omit({ sort: true }).extend({
    type: z
        .string()
        .optional()
        .transform((value) => value === undefined
        ? undefined
        : value
            .split(',')
            .map((entry) => entry.trim())
            .filter((entry) => entry.length > 0))
        .pipe(z.array(activityTypeSchema).optional()),
    source: z
        .string()
        .optional()
        .transform((value) => value
        ?.split(',')
        .map((entry) => entry.trim())
        .filter(Boolean))
        .pipe(z.array(activitySourceSchema).optional()),
    direction: callDirectionSchema.optional(),
    dateFrom: isoDateTimeSchema.optional(),
    dateTo: isoDateTimeSchema.optional(),
    personalOnly: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .default(false)
        .transform((value) => value === true || value === 'true'),
});
export const activityListResponseSchema = z.object({
    data: z.array(activitySchema),
    meta: paginationMetaSchema,
});
//# sourceMappingURL=customer-interaction.js.map